<template>
  <div class="btn-create-data-export d-inline-flex align-center flex-wrap" style="gap: 8px;">
    <template v-if="user_allowed">
      <v-menu
        v-for="fmt in visibleFormats"
        :key="fmt"
        offset-y
        left
        :close-on-content-click="true"
      >
        <template v-slot:activator="{ on: menuOn, attrs }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: tipOn }">
              <v-btn
                v-if="!asButton"
                icon
                :small="small"
                :color="activatorColor(fmt)"
                :disabled="disabled || Boolean(loadingFormat)"
                :loading="loadingFormat === fmt"
                v-bind="attrs"
                v-on="{ ...menuOn, ...tipOn }"
              >
                <v-icon>{{ formatMeta[fmt].icon }}</v-icon>
              </v-btn>
              <v-btn
                v-else
                :small="small"
                :outlined="activatorOutlined"
                :depressed="!activatorOutlined"
                :color="activatorColor(fmt)"
                :class="{ 'white--text': asButton && !lightToolbar }"
                :disabled="disabled || Boolean(loadingFormat)"
                :loading="loadingFormat === fmt"
                v-bind="attrs"
                v-on="{ ...menuOn, ...tipOn }"
              >
                <v-icon left :small="small">{{ formatMeta[fmt].icon }}</v-icon>
                {{ formatMeta[fmt].buttonLabel }}
              </v-btn>
            </template>
            <span>{{ formatMeta[fmt].tooltip }}</span>
          </v-tooltip>
        </template>
        <v-list dense class="py-1">
          <v-list-item @click="createDataExport(fmt)">
            <v-list-item-icon>
              <v-icon small color="primary">{{ formatMeta[fmt].actionIcon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Nova exportação {{ formatMeta[fmt].label }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="my-1" />
          <v-list-item :to="{ name: 'DataExport' }">
            <v-list-item-icon>
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Ver exportações</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <v-dialog v-model="loading" persistent width="500px" transition="dialog-bottom-transition">
      <v-alert type="info" :value="true" class="my-0">
        Exportando…
        <v-progress-linear color="white" indeterminate />
      </v-alert>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="700px" transition="dialog-transition">
      <v-card class="rounded-lg">
        <v-card-title class="subtitle-1 font-weight-bold pa-4 pb-2">
          Exportação
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 pb-2">
          <p class="mb-4">{{ msg }}</p>
          <v-btn :to="{ name: 'DataExport' }" color="primary" small depressed>
            Ver exportações
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end pa-3">
          <f-btn close @click="dialog = false"></f-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Api from '@/api/index';

export default {
  name: 'BtnCreateDataExport',
  props: {
    date_obj: {
      type: Object,
    },
    api_model: {
      type: String,
    },
    action: {
      type: String,
      default: '',
      required: true,
    },
    filters: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hide_list: {
      type: Array,
      default: () => [],
    },
    file_name: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    /** Se true, ícones coloridos (toolbar clara). Se false, botões brancos (header escuro do IndexCard). */
    lightToolbar: {
      type: Boolean,
      default: false,
    },
    /**
     * Se true, ativadores viram botões com rótulo (Excel / PDF), mais visíveis que só ícone.
     * Toolbar escura: outlined branco; toolbar clara: preenchido nas cores do formato.
     */
    asButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      msg: '',
      loadingFormat: null,
      formatMeta: {
        xlsx: {
          icon: 'mdi-microsoft-excel',
          tooltip: 'Exportar em Excel',
          label: '(.xlsx)',
          buttonLabel: 'Excel',
          actionIcon: 'mdi-microsoft-excel',
        },
        pdf: {
          icon: 'mdi-file-pdf-box',
          tooltip: 'Exportar em PDF',
          label: '(.pdf)',
          buttonLabel: 'PDF',
          actionIcon: 'mdi-file-pdf-box',
        },
      },
    };
  },
  computed: {
    user_allowed() {
      return this.has_roles(['Manager', 'Director', 'SalesExecutive', 'Financial', 'Analyst', 'CreditAnalyst']);
    },
    visibleFormats() {
      const out = [];
      if (!this.hide_list.includes('xlsx')) out.push('xlsx');
      if (!this.hide_list.includes('pdf')) out.push('pdf');
      return out;
    },
    /** Toolbar escura + botão: contorno branco; toolbar clara: preenchido. */
    activatorOutlined() {
      return this.asButton && !this.lightToolbar;
    },
    ...mapState({
      profile: (state) => state.User.oneUser,
    }),
  },
  methods: {
    sendAlert() {
      return confirm(
        'O limite máximo de exportação é de 200 itens. \n\nAjuste os filtros de exibição para uma quantidade igual ou inferior a 200 itens antes de prosseguir com a exportação.'
      );
    },
    activatorColor(fmt) {
      if (!this.lightToolbar) return 'white';
      return fmt === 'xlsx' ? 'success' : 'error';
    },
    createDataExport(format) {
      this.loading = true;
      this.loadingFormat = format;

      const listingFilters = { ...(this.filters || {}) };
      const params = {
        ...this.date_obj,
        ...listingFilters,
        modality_ids: [this.activeModalityId],
        modality_id: this.activeModalityId,
      };

      if (params.portfolio_id == null || params.portfolio_id === '') {
        delete params.portfolio_id;
      }

      const export_data = {
        user_id: this.profile.data.attributes.id,
        status: 'requested',
        klass: this.api_model,
        action: this.action,
        file_name: this.file_name,
        params,
        format: format,
      };
      Api.DataExport.create(export_data)
        .then(() => {
          this.msg = 'Solicitação realizada. Acesse a seção de exportações para baixar o arquivo.';
        })
        .catch((err) => {
          const errMsg = err.response && err.response.data && err.response.data.error;
          this.msg = errMsg || 'Não foi possível solicitar a exportação.';
          console.log(errMsg || err);
        })
        .finally(() => {
          this.dialog = true;
          this.loading = false;
          this.loadingFormat = null;
        });
    },
  },
};
</script>
